/**
 * Currency formatter for Vitapur theme
 * Handles consistent currency formatting across the site
 * Supports multiple markets and currencies
 */

class CurrencyFormatter {
    constructor(options = {}) {
        this.moneyFormat =
            options.moneyFormat || "{{amount_with_comma_separator}} €";
        this.moneyWithCurrencyFormat =
            options.moneyWithCurrencyFormat ||
            "{{amount_with_comma_separator}} EUR";

        this.defaultLocale = options.locale || "lt";
        this.defaultCurrency = options.currencyCode || "EUR";

        this.locale = `${
            this.defaultLocale
        }-${this.defaultLocale.toUpperCase()}`;
        this.currency = this.defaultCurrency;

        this.currencySymbols = {
            EUR: "€",
            USD: "$",
            GBP: "£",
            PLN: "zł",
            CZK: "Kč",
            HUF: "Ft",
            RON: "lei",
            BGN: "лв",
            HRK: "kn",
        };

        // Currency format mapping (expand as needed)
        this.currencyFormats = {
            EUR: {
                default: "{{amount_with_comma_separator}}",
                with_currency: "{{amount_with_comma_separator}} €",
            },
            USD: {
                default: "{{amount_with_comma_separator}}",
                with_currency: "{{amount_with_comma_separator}} $",
            },
            GBP: {
                default: "{{amount_with_comma_separator}}",
                with_currency: "{{amount_with_comma_separator}} £",
            },
        };

        this.localeFormats = {
            "lt-LT": { decimal: ",", thousands: "." },
            "en-US": { decimal: ".", thousands: "," },
            "de-DE": { decimal: ",", thousands: "." },
            "fr-FR": { decimal: ",", thousands: " " },
        };

    }
    /**
     * Set the active currency and locale for the formatter
     * @param {string} currency - Currency code (e.g., 'EUR', 'USD')
     * @param {string} locale - Locale code (e.g., 'lt-LT', 'en-US')
     */
    setCurrency(currency, locale = null) {
        if (currency) {
            this.currency = currency;
        }

        if (locale) {
            this.locale = locale;
        }

        return this;
    }

    /**
     * Get currency symbol for current or specified currency
     * @param {string} currencyCode - Optional currency code
     * @return {string} Currency symbol
     */
    getCurrencySymbol(currencyCode = null) {
        const currency = currencyCode || this.currency;
        return this.currencySymbols[currency] || currency;
    }

    /**
     * Format money value according to shop's currency format
     * @param {number|string} cents - Amount in cents
     * @param {Object} options - Formatting options
     * @return {string} Formatted money string
     */
    formatMoney(cents, options = {}) {
        if (typeof cents === "string") {
            cents = cents.replace(".", "");
        }

        if (isNaN(cents) || cents === null) {
            cents = 0;
        }

        const amount = parseInt(cents || 0) / 100;
        const currencyCode = options.currency || this.currency;
        const currencySymbol = this.getCurrencySymbol(currencyCode);

        // For European style (common in Lithuania, etc.)
        // Format without currency symbol first
        const formatOptions = {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        };

        try {
            // Format the number without currency symbol
            const formattedNumber = new Intl.NumberFormat(this.locale, formatOptions).format(amount);
            
            // Add the currency symbol according to locale conventions
            // For most European countries (except UK), place the symbol after the amount
            if (this.locale === "en-US" || this.locale === "en-GB") {
                return currencySymbol + formattedNumber;
            } else {
                return formattedNumber + " " + currencySymbol;
            }
        } catch (e) {
            console.warn("Error formatting currency with Intl API:", e);
            return this.fallbackFormatMoney(amount, currencyCode);
        }
    }

    /**
     * Fallback for environments where Intl.NumberFormat might not be available
     * @param {number} amount - Amount as a decimal number
     * @param {string} currencyCode - Currency code
     * @return {string} Formatted money string
     */
    fallbackFormatMoney(amount, currencyCode = null) {
        const currency = currencyCode || this.currency;
        const symbol = this.getCurrencySymbol(currency);

        // Get locale-specific separators
        const separators =
            this.localeFormats[this.locale] ||
            this.localeFormats[this.defaultLocale];

        // Format number with appropriate separators
        const parts = amount.toFixed(2).split(".");
        const wholePart = parts[0].replace(
            /\B(?=(\d{3})+(?!\d))/g,
            separators.thousands
        );
        const decimalPart = parts[1];

        // Place symbol according to locale convention
        if (this.locale === "en-US" || this.locale === "en-GB") {
            return symbol + wholePart + separators.decimal + decimalPart;
        } else {
            return wholePart + separators.decimal + decimalPart + " " + symbol;
        }
    }

    /**
     * Format money without currency symbol
     * @param {number|string} cents - Amount in cents
     * @return {string} Formatted money string without currency symbol
     */
    formatMoneyWithoutCurrency(cents) {
        if (typeof cents === "string") {
            cents = cents.replace(".", "");
        }

        if (isNaN(cents) || cents === null) {
            cents = 0;
        }

        const amount = parseInt(cents || 0) / 100;

        // Get locale-specific decimal separator
        const decimalSeparator = (
            this.localeFormats[this.locale] ||
            this.localeFormats[this.defaultLocale]
        ).decimal;

        return amount.toFixed(2).replace(".", decimalSeparator);
    }
}
// Create a global instance for the theme to use
window.vitapurCurrency = new CurrencyFormatter({
    moneyFormat: window.theme?.moneyFormat,
    moneyWithCurrencyFormat: window.theme?.moneyWithCurrencyFormat,
    locale: window.theme?.locale || "lt-LT",
    currency: window.theme?.currencyCode || "EUR",
});
